import {
  getActiveCampaigns,
  getCampaignByProduct,
  getCampaignConfig,
} from 'content/campaigns/lookup';
import type { Campaign } from 'content/campaigns/types';
import {
  applyBestPromo,
  promoDiscountAmount,
  type PromoDiscount,
} from 'lib/pricing/promoDiscount';

export type DisplayedPrice = {
  listPrice: number;
  salePrice: number;
  discountCode?: string;
  /** Promos that produced `salePrice`. Shop cards reapply them per color. */
  promos: PromoDiscount[];
};

const GIFT_CARD_PRODUCT = 'gift-card';

type PricedProduct = Campaign['product'] | 'offer';

function keepsACharge(listPrice: number, promo: PromoDiscount): boolean {
  const amount = promoDiscountAmount(listPrice, promo);
  return amount > 0 && amount < listPrice;
}

/**
 * One product, one base offer. `listPrice` is the variant Shopify will charge.
 * The sale and `discountCode` come from that product's active campaign row.
 * A flat (or 100%) discount that would wipe the price is dropped: Shopify can
 * reject the code and charge full price. `gift-card` ignores `extraPromos`.
 */
export function resolveDisplayedPrice({
  product,
  listPrice,
  campaigns = [],
  extraPromos = [],
}: {
  product: PricedProduct;
  listPrice: number;
  campaigns?: Campaign[];
  extraPromos?: PromoDiscount[];
}): DisplayedPrice {
  const price = Number.isFinite(listPrice) && listPrice > 0 ? listPrice : 0;
  const promos: PromoDiscount[] = [];
  let discountCode: string | undefined;

  const config = getCampaignConfig(
    getCampaignByProduct(getActiveCampaigns(campaigns), product),
  );
  const code = config?.discountCode?.trim() ?? '';
  if (config && code) {
    const promo: PromoDiscount = {
      discount: config.discount,
      discountType: config.discountType,
      active: true,
    };
    if (keepsACharge(price, promo)) {
      promos.push(promo);
      discountCode = code;
    }
  }

  if (product !== GIFT_CARD_PRODUCT) {
    for (const promo of extraPromos) {
      if (keepsACharge(price, promo)) promos.push(promo);
    }
  }

  return {
    listPrice: price,
    salePrice: applyBestPromo(price, promos),
    discountCode,
    promos,
  };
}

/** Discount the buy page should show: the live campaign when it has a code
 *  Shopify can apply, otherwise the static fallback. */
export function campaignDiscountAmount({
  product,
  listPrice,
  campaigns = [],
  fallback,
}: {
  product: PricedProduct;
  listPrice: number;
  campaigns?: Campaign[];
  fallback?: PromoDiscount | null;
}): number {
  const resolved = resolveDisplayedPrice({ product, listPrice, campaigns });
  if (resolved.discountCode) return listPrice - resolved.salePrice;
  return promoDiscountAmount(listPrice, fallback);
}

/** Promos for a product, independent of any one variant's price. Cards
 *  reapply them per color so a flat code that would zero one price is dropped there. */
export function promosFor(
  product: PricedProduct,
  campaigns: Campaign[] = [],
  extraPromos: PromoDiscount[] = [],
): PromoDiscount[] {
  return resolveDisplayedPrice({
    product,
    listPrice: Number.MAX_SAFE_INTEGER,
    campaigns,
    extraPromos,
  }).promos;
}

/** One-time M1 price. A UHX bundle replaces the campaign, matching the buy page. */
export function m1PlanSale(
  listPrice: number,
  promos: PromoDiscount[] = [],
  uhxFraction = 0,
): number {
  if (uhxFraction > 0) return listPrice * (1 - uhxFraction);
  if (promos.length > 0) return salePriceFor(listPrice, promos);
  return listPrice;
}

/** Reapply a promo set to a specific variant price, dropping a code that
 *  would zero that price. */
export function salePriceFor(
  listPrice: number,
  promos: PromoDiscount[] = [],
): number {
  return resolveDisplayedPrice({
    product: 'offer',
    listPrice,
    extraPromos: promos,
  }).salePrice;
}
